import { useRef, useState } from 'react';
import classNames from 'classnames';
import { tw } from 'twind';
import Slider, { Settings } from 'react-slick';
import NextArrow from '@/components/NewCart/NextArrow';
import PrevArrow from '@/components/NewCart/PrevArrow';
import Dot from '@/components/NewCart/Dot/Dot';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

const ImageCarousel: React.FC<{
  urls: string[];
  onClick: (index: number) => void;
  withControls?: boolean;
  withFullWidth?: boolean;
}> = ({ urls, onClick, withControls = true, withFullWidth = false }) => {
  const ref = useRef<Slider | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const settings: Settings = {
    dots: true,
    infinite: true,
    fade: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: withControls ? <NextArrow /> : undefined,
    prevArrow: withControls ? <PrevArrow /> : undefined,
    beforeChange: (current, next) => setCurrentSlide(next),
    appendDots: (dots: Array<React.ReactElement>) => {
      const lastSlide = dots.length - 1;
      const isFirstSlide = currentSlide === 0;
      const isLastSlide = currentSlide === lastSlide;
      const withCounter = dots.length > 6;

      const handleToggle = (next: number) => {
        if (!(ref && ref.current)) return;
        setCurrentSlide(next);
        ref.current.slickGoTo(next);
      };

      const handleIncrement = () => {
        const next = isLastSlide ? 0 : currentSlide + 1;
        handleToggle(next);
      };

      const handleDecrement = () => {
        const next = isFirstSlide ? lastSlide : currentSlide - 1;
        handleToggle(next);
      };

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            bottom: '20px',
          }}
        >
          <ul
            className={classNames(
              tw`flex justify-center items-center w-[fit-content] bg-[#EFEFEF] rounded-[4px] gap-[8px] px-[8px] py-[6px]`,
              { [tw`px-[4px] py-[0]`]: withCounter }
            )}
          >
            {withCounter ? (
              <>
                <Dot onClick={handleDecrement} isCurrent={isFirstSlide} />
                <div className={tw`text-[12px]`}>
                  {currentSlide + 1}/{dots.length}
                </div>
                <Dot onClick={handleIncrement} isCurrent={isLastSlide} />
              </>
            ) : (
              dots.map((item, index) => item.props.children)
            )}
          </ul>
        </div>
      );
    },
    customPaging: (i) => <Dot isCurrent={i === currentSlide} />,
  };

  return (
    <Slider {...settings} ref={ref}>
      {urls.map((url, index) => (
        <div key={url}>
          <img
            className={withFullWidth ? tw`w-full h-full object-cover` : tw`max-w-[250px] max-h-[375px] m-auto`}
            src={url}
            onClick={() => onClick(index)}
            onTouchMove={(e) => {
              if (e.touches.length !== 2) return;
              onClick(index);
            }}
          />
        </div>
      ))}
    </Slider>
  );
};

export default ImageCarousel;
